import {
  type DocumentData,
  type QueryDocumentSnapshot,
  collection,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { getFirebase } from "./init.js";

export interface FsDocValue<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export interface FsCollectionValue<T> {
  /** Array of docs with their id spread into each entry. */
  data: Array<T & { id: string }>;
  loading: boolean;
  error: Error | null;
}

/**
 * Subscribe to a single Firestore document. Path must have even depth,
 * e.g. "tables/t1" or "tables/t1/scenes/s1". Pass null to skip the
 * subscription (useful before tableId is known).
 */
export function useFirestoreDoc<T = DocumentData>(path: string | null): FsDocValue<T> {
  const [state, setState] = useState<FsDocValue<T>>({ data: null, loading: true, error: null });
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      lastPath.current = null;
      return undefined;
    }
    if (lastPath.current !== path) {
      lastPath.current = path;
      setState({ data: null, loading: true, error: null });
    }
    const { db } = getFirebase();
    const unsub = onSnapshot(
      doc(db, path),
      (snap) => {
        setState({
          data: snap.exists() ? ((snap.data() as T | undefined) ?? null) : null,
          loading: false,
          error: null,
        });
      },
      (err) => setState({ data: null, loading: false, error: err }),
    );
    return () => unsub();
  }, [path]);

  return state;
}

/**
 * Subscribe to a Firestore collection (odd-depth path). Each returned row
 * is the doc data with `id` merged in.
 */
export function useFirestoreCollection<T = DocumentData>(
  path: string | null,
): FsCollectionValue<T> {
  const [state, setState] = useState<FsCollectionValue<T>>({
    data: [],
    loading: true,
    error: null,
  });
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!path) {
      setState({ data: [], loading: false, error: null });
      lastPath.current = null;
      return undefined;
    }
    if (lastPath.current !== path) {
      lastPath.current = path;
      setState({ data: [], loading: true, error: null });
    }
    const { db } = getFirebase();
    const unsub = onSnapshot(
      collection(db, path),
      (snap) => {
        const rows: Array<T & { id: string }> = snap.docs.map((d: QueryDocumentSnapshot) => ({
          ...(d.data() as T),
          id: d.id,
        }));
        setState({ data: rows, loading: false, error: null });
      },
      (err) => setState({ data: [], loading: false, error: err }),
    );
    return () => unsub();
  }, [path]);

  return state;
}
